import {gql} from "@apollo/client";
import ComponentState, {ComponentStateTypes} from "components/common/ComponentState";
import NoData from "components/common/NoData";
import BooleanView from "components/common/views/BooleanView";
import ItemView, {ItemViewPropsType} from "components/common/views/ItemView";
import {graph} from "config/axios-context";
import {useEffect, useState} from "react";
import {BookingAdditionalChargesViewDataQuery, BookingAdditionalChargesViewDataQueryVariables} from "types/graphql";
import {DateFormats, DateUtil} from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type Model = {
    id: number,
    name: string,
    amount: number,
    date: Date,
    note?: string | null,
    isIncome: boolean,
    booking: {
        id: number,
        inSeasonId: number
    }
}

type BookingAdditionalChargesViewPropsType = {
    model?: Model | string | number,
    name: string
}

const query = gql`
query BookingAdditionalChargesViewData($id: Int) {
    bookingAdditionalCharge(id: $id) {
        amount
        date
        name
        id
        note
        isIncome
        booking {
            id
            inSeasonId
      }
    }
}
`;

const BookingAdditionalChargesView = (props: BookingAdditionalChargesViewPropsType) => {
    const context = useAppContext();
    const [model, setModel] = useState<Model | undefined>(typeof props.model === 'object' ? props.model : undefined);
    const [componentState, setComponentState] = useState<ComponentStateTypes>(typeof props.model === 'object' ? '' : 'loading');

    // #region load
    useEffect(() => {
        if (typeof props.model === 'string' || typeof props.model === 'number') {
            load();
        }
    }, [props.model]);

    const load = async () => {
        setComponentState('loading');
        try {
            const response = await graph<BookingAdditionalChargesViewDataQuery, BookingAdditionalChargesViewDataQueryVariables>({
                query,
                variables: {
                    id: Number(props.model)
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            if (response.data?.bookingAdditionalCharge) {
                setModel(response.data.bookingAdditionalCharge as Model);
                setComponentState('');
            } else {
                setComponentState('nodata');
            }
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    };
    // #endregion load

    const view = () => {
        if (!model) {
            return <NoData/>;
        }

        const viewData: ItemViewPropsType['model'] = [
            {
                name: 'name',
                items: [
                    {
                        name: 'id',
                        value: model.id,
                        label: 'fields.id.name'
                    },
                    {
                        name: 'name',
                        value: model.name,
                        label: 'fields.name.name'
                    },
                    {
                        name: 'amount',
                        value: model.amount,
                        label: 'fields.amount.name'
                    },
                    {
                        name: 'isIncome',
                        value: <BooleanView value={model.isIncome}/>,
                        label: 'fields.isIncome.name'
                    },
                    {
                        name: 'date',
                        value: DateUtil.format(model.date, DateFormats.LONG),
                        label: 'fields.date.name'
                    },
                    {
                        name: 'note',
                        label: 'fields.note.name',
                        value: <pre>
                            {model.note || "-"}
                        </pre>
                    }
                ]
            }
        ];

        return <ItemView model={viewData} view="default" name={props.name + "-booking-additional-charges"}/>;
    }

    return (
        <ComponentState state={componentState}>
            {view()}
        </ComponentState>
    );
};

export default BookingAdditionalChargesView;
